import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, FlaskConical, Loader2, Send, X } from "lucide-react";
import { api, type ConfigChange, type SandboxComparison, type SandboxFigures, type SandboxInfo, type SandboxProposal } from "../lib/api";
import { Section } from "./Section";

/**
 * Sandboxes: where an administrator tries rules before they reach the board.
 *
 * A sandbox is a copy of the settings planned against the same flights as Live. Rules are
 * worked out with the assistant or edited in Settings while the sandbox is switched on; a
 * rule the assistant drafts is a proposal until someone here presses Apply; Compare plans
 * the last 30 days and the next 7 under both; Promote makes the sandbox's settings Live's.
 */

const card = "rounded-xl border border-line p-4";
const btn = "rounded-lg border border-line px-2.5 py-1 text-[12.5px] font-semibold hover:bg-panel-2 disabled:opacity-40";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-top";

/** The strip under the top bar while a sandbox is on, so nobody mistakes it for Live. */
export function SandboxBanner({ name, onLive, onOpen }: { name: string; onLive: () => void; onOpen: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-warn/50 bg-warn/15 px-4 py-1.5 text-[12.5px] text-warn">
      <FlaskConical className="size-4 shrink-0" />
      <span className="font-semibold">{`Sandbox: ${name}.`}</span>
      <span className="text-ink-2">Plans and settings here never reach Live, texts are not sent, and nothing is fixed automatically.</span>
      <span className="ml-auto flex gap-2">
        <button className={btn} onClick={onOpen}>
          Sandbox page
        </button>
        <button className={clsx(btn, "border-warn text-warn")} onClick={onLive}>
          Back to Live
        </button>
      </span>
    </div>
  );
}

const show = (v: unknown): string => {
  if (v == null) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  const s = JSON.stringify(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
};

export function DiffTable({ changes, left = "Before", right = "After" }: { changes: ConfigChange[]; left?: string; right?: string }) {
  if (!changes.length) return <p className="text-[12.5px] text-ink-3">No differences.</p>;
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>Setting</th>
            <th className={th}>{left}</th>
            <th className={th}>{right}</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((c, i) => (
            <tr key={`${c.path}${i}`} className="border-b border-line">
              <td className={clsx(td, "font-semibold")}>{c.path}</td>
              <td className={clsx(td, "tabular break-all text-ink-2")}>{show(c.before)}</td>
              <td className={clsx(td, "tabular break-all")}>{show(c.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SandboxPage({ current, onPick, onBoard }: { current: number | null; onPick: (id: number | null) => void; onBoard: () => void }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["sandboxes"], queryFn: () => api.sandboxes() });
  const [name, setName] = useState("");
  const [open, setOpen] = useState<number | null>(current);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["sandboxes"] });
  const act = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: () => {
      setError(null);
      refresh();
      void qc.invalidateQueries({ queryKey: ["sandbox"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const sandboxes = list.data?.sandboxes ?? [];
  const shown = sandboxes.find((s) => s.id === open) ?? sandboxes.find((s) => s.status === "active") ?? null;

  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button className={btn} onClick={onBoard}>
          <ArrowLeft className="inline size-3.5" /> Board
        </button>
        <h1 className="flex items-center gap-2 text-[18px] font-bold">
          <FlaskConical className="size-5 text-ink-3" /> Sandboxes
        </h1>
      </div>
      <p className="mb-4 max-w-3xl text-[13.5px] text-ink-2">
        A sandbox is a copy of the settings to try rules in, planned against the same flights as Live. Nothing in it reaches the board until an administrator
        promotes it. Two per airport; the second waits for a super administrator.
      </p>
      {error && <p className="mb-3 text-[12.5px] text-bad">{error}</p>}

      <div className="mb-5 grid gap-3 md:grid-cols-3">
        {sandboxes.map((s) => (
          <SandboxCard
            key={s.id}
            s={s}
            current={current === s.id}
            canApprove={!!list.data?.canApprove}
            onOpen={() => setOpen(s.id)}
            onSwitch={() => onPick(current === s.id ? null : s.id)}
            onApprove={() => act.mutate(() => api.approveSandbox(s.id))}
            onArchive={() => {
              if (window.confirm(`Archive "${s.name}"? Its settings and plans are kept but it can no longer be worked in.`)) {
                if (current === s.id) onPick(null);
                act.mutate(() => api.archiveSandbox(s.id));
              }
            }}
          />
        ))}
        {sandboxes.length < 2 && (
          <div className={clsx(card, "flex flex-col gap-2")}>
            <div className="text-[13px] font-semibold">New sandbox</div>
            <div className="text-[11.5px] text-ink-3">
              {sandboxes.length === 0 ? "Copied from Live's settings as they are now." : "The second sandbox needs a super administrator's approval before it can be used."}
            </div>
            <input className="rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" placeholder="Name, e.g. Winter rules" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
            <button
              className={btn}
              disabled={act.isPending}
              onClick={() =>
                act.mutate(async () => {
                  const s = await api.createSandbox(name);
                  setName("");
                  setOpen(s.id);
                })
              }
            >
              Create
            </button>
          </div>
        )}
      </div>

      {shown && shown.status === "active" && <SandboxDetail key={shown.id} s={shown} canDesign={!!list.data?.canDesign} />}
      {shown && shown.status === "pending" && <p className="text-[13px] text-ink-3">{`"${shown.name}" is waiting for a super administrator's approval.`}</p>}
    </div>
  );
}

function SandboxCard(p: { s: SandboxInfo; current: boolean; canApprove: boolean; onOpen: () => void; onSwitch: () => void; onApprove: () => void; onArchive: () => void }) {
  const { s } = p;
  return (
    <div className={clsx(card, "flex flex-col gap-1.5", p.current && "border-warn")}>
      <button className="text-left text-[14px] font-bold hover:underline" onClick={p.onOpen}>
        {s.name}
      </button>
      <div className="text-[11.5px] text-ink-3">
        {`Sandbox ${s.slot} · made by ${s.createdBy} ${DateTime.fromISO(s.createdAt).toFormat("d LLL")}`}
        {s.status === "pending" ? " · waiting for approval" : s.approvedBy && s.slot === 2 ? ` · approved by ${s.approvedBy}` : ""}
      </div>
      <div className="mt-1 flex flex-wrap gap-2">
        {s.status === "active" && (
          <button className={clsx(btn, p.current && "border-warn text-warn")} onClick={p.onSwitch}>
            {p.current ? "Back to Live" : "Work in this sandbox"}
          </button>
        )}
        {s.status === "pending" && p.canApprove && (
          <button className={btn} onClick={p.onApprove}>
            Approve
          </button>
        )}
        <button className={clsx(btn, "text-ink-3")} onClick={p.onArchive}>
          Archive
        </button>
      </div>
    </div>
  );
}

interface Line {
  role: "user" | "assistant";
  content: string;
  proposals?: SandboxProposal[];
}

function SandboxDetail({ s, canDesign }: { s: SandboxInfo; canDesign: boolean }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["sandbox", s.id], queryFn: () => api.sandbox(s.id) });
  const [lines, setLines] = useState<Line[]>([]);
  const [draft, setDraft] = useState("");
  const [compare, setCompare] = useState<SandboxComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ["sandbox", s.id] });
    void qc.invalidateQueries({ queryKey: ["sandboxes"] });
  };
  const talk = useMutation({
    mutationFn: (message: string) => api.talkSandbox(s.id, message, lines.map(({ role, content }) => ({ role, content }))),
    onMutate: (message) => setLines((l) => [...l, { role: "user", content: message }]),
    onSuccess: (r) => {
      setLines((l) => [...l, { role: "assistant", content: r.answer ?? "(no answer)", proposals: r.proposals }]);
      reload();
    },
    onError: (e) => setError((e as Error).message),
  });
  const act = useMutation({
    mutationFn: (f: () => Promise<unknown>) => f(),
    onSuccess: () => {
      setError(null);
      reload();
    },
    onError: (e) => setError((e as Error).message),
  });
  const runCompare = useMutation({ mutationFn: () => api.compareSandbox(s.id), onSuccess: setCompare, onError: (e) => setError((e as Error).message) });
  const d = detail.data;
  const open = (d?.proposals ?? []).filter((p) => p.status === "open");

  return (
    <>
      {error && <p className="mb-3 text-[12.5px] text-bad">{error}</p>}

      <Section title="Work out a rule" caption="Talk it through with the assistant. When you agree, it writes the change up as a proposal; nothing changes in the sandbox until you press Apply.">
        {!canDesign ? (
          <p className="text-[12.5px] text-ink-3">The assistant needs an Anthropic API key in Secrets. Rules can still be changed in Settings while this sandbox is switched on.</p>
        ) : (
          <div className={card}>
            <div className="mb-3 flex max-h-[28rem] flex-col gap-2 overflow-auto">
              {lines.length === 0 && (
                <p className="text-[12.5px] text-ink-3">
                  For example: &ldquo;Keep Gate 10 for Flair after 18:00&rdquo;, &ldquo;Try 15 minutes between pushbacks on neighbouring gates&rdquo;, or &ldquo;Why do Air North
                  jets end up on Gate 10?&rdquo;
                </p>
              )}
              {lines.map((l, i) => (
                <div key={i} className={clsx("max-w-3xl rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap", l.role === "user" ? "self-end bg-info/10" : "bg-panel-2")}>
                  {l.content}
                  {l.proposals?.map((p) => (
                    <ProposalCard key={p.id} p={p} onApply={() => act.mutate(() => api.applyProposal(s.id, p.id))} onDrop={() => act.mutate(() => api.dropProposal(s.id, p.id))} />
                  ))}
                </div>
              ))}
              {talk.isPending && <Loader2 className="size-4 animate-spin text-ink-3" />}
            </div>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim() && !talk.isPending) {
                  talk.mutate(draft.trim());
                  setDraft("");
                }
              }}
            >
              <input className="flex-1 rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Describe the rule you want to try" maxLength={4000} />
              <button className={btn} disabled={!draft.trim() || talk.isPending} aria-label="Send">
                <Send className="size-4" />
              </button>
            </form>
          </div>
        )}
      </Section>

      {open.length > 0 && (
        <Section title="Waiting for you" caption="Changes the assistant proposed that have not been applied or dropped.">
          <div className="flex flex-col gap-2">
            {open.map((p) => (
              <ProposalCard key={p.id} p={p} onApply={() => act.mutate(() => api.applyProposal(s.id, p.id))} onDrop={() => act.mutate(() => api.dropProposal(s.id, p.id))} />
            ))}
          </div>
        </Section>
      )}

      <Section title="What differs from Live?" caption="Every setting this sandbox has changed, Live's value beside it.">
        {d ? <DiffTable changes={d.diff} left="Live" right="This sandbox" /> : <Loader2 className="size-4 animate-spin text-ink-3" />}
      </Section>

      <Section
        title="How would it have gone?"
        caption="The last 30 days and the next 7, each planned from scratch twice on the same flights: once under Live's rules, once under this sandbox's. The difference is the rules and nothing else."
      >
        <button className={btn} disabled={runCompare.isPending} onClick={() => runCompare.mutate()}>
          {runCompare.isPending ? "Planning every day twice…" : compare ? "Compare again" : "Compare with Live"}
        </button>
        {compare && <CompareView c={compare} />}
      </Section>

      <Section title="Make it Live" caption="The sandbox's settings become Live's as a new settings version, which can be stepped back from under Settings → Change history.">
        <Promote s={s} changes={d?.diff.length ?? 0} onDone={reload} />
      </Section>

      <Section title="This sandbox's versions" caption="Every change to it, newest first. Going back is itself a new version.">
        <table className="w-full max-w-3xl text-[12.5px]">
          <tbody>
            {(d?.versions ?? []).map((v, i) => (
              <tr key={v.id} className="border-b border-line">
                <td className={clsx(td, "tabular text-ink-3")}>{DateTime.fromISO(v.at).toFormat("d LLL HH:mm")}</td>
                <td className={td}>{v.note}</td>
                <td className={clsx(td, "text-ink-2")}>{v.by}</td>
                <td className={td}>
                  {i > 0 && (
                    <button className={btn} onClick={() => act.mutate(() => api.restoreSandbox(s.id, v.id))}>
                      Go back to this
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </>
  );
}

function ProposalCard({ p, onApply, onDrop }: { p: SandboxProposal; onApply: () => void; onDrop: () => void }) {
  return (
    <div className="mt-2 rounded-lg border border-line bg-panel p-3 text-[12.5px] whitespace-normal">
      <div className="mb-1 font-semibold">{`Proposed: ${p.summary}`}</div>
      <DiffTable changes={p.diff} left="Now" right="Proposed" />
      {p.status === "open" ? (
        <div className="mt-2 flex gap-2">
          <button className={clsx(btn, "border-ok text-ok")} onClick={onApply}>
            <Check className="inline size-3.5" /> Apply to this sandbox
          </button>
          <button className={btn} onClick={onDrop}>
            <X className="inline size-3.5" /> Drop
          </button>
        </div>
      ) : (
        <div className="mt-1 text-[11.5px] text-ink-3">{p.status === "applied" ? "Applied." : "Dropped."}</div>
      )}
    </div>
  );
}

const ROWS: Array<{ label: string; of: (f: SandboxFigures) => number; better: "up" | "down"; unit?: string }> = [
  { label: "Plan quality (average)", of: (f) => f.score.quality, better: "up" },
  { label: "Hard conflicts", of: (f) => f.score.hardConflicts, better: "down" },
  { label: "Aircraft with no stand", of: (f) => f.unallocated, better: "down" },
  { label: "Jets off a bridge while one was free", of: (f) => f.jetsOffBridge, better: "down" },
  { label: "Pushback pairs too close", of: (f) => f.pushbackPairs, better: "down" },
  { label: "Passengers by jet bridge (average)", of: (f) => f.score.jetbridgePaxPct, better: "up", unit: "%" },
  { label: "Preferences met", of: (f) => f.score.preferenceMet, better: "up" },
  { label: "Warnings", of: (f) => f.score.warnings, better: "down" },
];

export function CompareView({ c }: { c: SandboxComparison }) {
  const [days, setDays] = useState(false);
  return (
    <div className="mt-3">
      <table className="w-full max-w-2xl text-[12.5px]">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>{`${c.days.length} days, ${c.from} to ${c.to}`}</th>
            <th className={th}>Live</th>
            <th className={th}>Sandbox</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => {
            const a = r.of(c.totals.live);
            const b = r.of(c.totals.sandbox);
            const better = r.better === "up" ? b > a : b < a;
            const worse = r.better === "up" ? b < a : b > a;
            return (
              <tr key={r.label} className="tabular border-b border-line">
                <td className={td}>{r.label}</td>
                <td className={td}>{`${a}${r.unit ?? ""}`}</td>
                <td className={clsx(td, "font-semibold", better && "text-ok", worse && "text-bad")}>{`${b}${r.unit ?? ""}`}</td>
              </tr>
            );
          })}
          <tr className="tabular">
            <td className={td}>Aircraft visits on a different stand</td>
            <td className={td} />
            <td className={clsx(td, "font-semibold")}>{c.totals.moved}</td>
          </tr>
        </tbody>
      </table>
      <button className={clsx(btn, "mt-2")} onClick={() => setDays((v) => !v)}>
        {days ? "Hide the days" : "Show each day"}
      </button>
      {days && (
        <table className="mt-2 w-full max-w-3xl text-[12px]">
          <thead>
            <tr className="border-b border-line">
              <th className={th}>Day</th>
              <th className={th}>Quality</th>
              <th className={th}>Hard conflicts</th>
              <th className={th}>Jets off bridge</th>
              <th className={th}>Pushback pairs</th>
              <th className={th}>Moved</th>
            </tr>
          </thead>
          <tbody>
            {c.days.map((d) => (
              <tr key={d.date} className="tabular border-b border-line">
                <td className={td}>{`${DateTime.fromISO(d.date).toFormat("ccc d LLL")}${d.when === "ahead" ? " (ahead)" : ""}`}</td>
                <td className={td}>{`${d.live.score.quality} → ${d.sandbox.score.quality}`}</td>
                <td className={td}>{`${d.live.score.hardConflicts} → ${d.sandbox.score.hardConflicts}`}</td>
                <td className={td}>{`${d.live.jetsOffBridge} → ${d.sandbox.jetsOffBridge}`}</td>
                <td className={td}>{`${d.live.pushbackPairs} → ${d.sandbox.pushbackPairs}`}</td>
                <td className={td}>{d.moved}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Promote({ s, changes, onDone }: { s: SandboxInfo; changes: number; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [done, setDone] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const go = useMutation({
    mutationFn: () => api.promoteSandbox(s.id, note),
    onSuccess: (r) => {
      setDone(r.id);
      onDone();
    },
    onError: (e) => setError((e as Error).message),
  });
  if (done != null) return <p className="text-[12.5px] text-ok">{`Live now runs on these settings (settings version ${done}).`}</p>;
  return (
    <div className="flex max-w-2xl flex-wrap items-center gap-2">
      <input className="flex-1 rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" placeholder="Why, for the change history" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
      <button
        className={clsx(btn, "border-warn text-warn")}
        disabled={!changes || go.isPending}
        onClick={() => {
          if (window.confirm(`Make "${s.name}" Live? ${changes} setting${changes === 1 ? "" : "s"} will change on the board for everyone.`)) go.mutate();
        }}
      >
        Promote to Live
      </button>
      {!changes && <span className="text-[11.5px] text-ink-3">Nothing differs from Live yet.</span>}
      {error && <span className="text-[12px] text-bad">{error}</span>}
    </div>
  );
}
